import { memoize } from 'lodash';
import { Node } from 'react-virtualized-tree';

import { ParentsMap } from '../../typings/feedCategories';
import { unescapeHtmlSymbolCodes } from '../Escape/Escape';

export class VirtualTreeHelpers {
    static findNodeById = memoize(
        <T extends Node>(id: string | number, nodes: Array<T>): T | undefined => {
            if (!nodes || nodes.length === 0) {
                return undefined;
            }

            let result;
            let node;
            for (let i = 0; !result && i < nodes.length; i++) {
                node = nodes[i];
                if (node.id === id) {
                    return node;
                }

                if (node.children && node.children.length !== 0) {
                    const cache = VirtualTreeHelpers.findNodeById.cache;
                    const stringId = String(id);

                    if (cache.has(stringId) && !cache.get(stringId)) {
                        cache.delete(stringId);
                    }

                    result = VirtualTreeHelpers.findNodeById(id, node.children);
                }
            }
            return result as T;
        },
        id => String(id)
    );

    static areChildrenEmpty(node: Node): boolean {
        return !node.children || node.children.length === 0;
    }

    static isEveryChildSelected(node: Node): boolean {
        if (node.children && node.children.length) {
            return node.children.every(child => child.state && child.state.selected);
        }

        return node.state && node.state.selected;
    }

    static isAnyChildSelected(node: Node): boolean {
        if (node.children && node.children.length) {
            return node.children.some(child => VirtualTreeHelpers.isAnyChildSelected(child));
        }

        return node.state && node.state.selected;
    }

    static selectChildren(nodes: Array<Node>, selected: boolean): Array<Node> {
        return nodes.map(n => ({
            ...n,
            children: n.children ? VirtualTreeHelpers.selectChildren(n.children, selected) : [],
            state: {
                ...n.state,
                selected,
                partialSelected: false
            }
        }));
    }

    static selectParents<T extends Node>(nodes: Array<T>): Array<T> {
        return nodes.map(node => {
            const isEveryChildSelected = VirtualTreeHelpers.isEveryChildSelected(node);

            return {
                ...node,
                state: {
                    ...node.state,
                    selected: isEveryChildSelected,
                    partialSelected: !isEveryChildSelected && VirtualTreeHelpers.isAnyChildSelected(node)
                },
                children: node.children ? VirtualTreeHelpers.selectParents(node.children) : []
            };
        });
    }

    static filterNodes<T extends Node>(nodes: Array<T>, filter: (node: T) => boolean): Array<T> {
        const resultPhrases: Array<T> = [];

        return nodes.reduce((filtered, node) => {
            const filteredChildren = node.children
                ? /* приведение типа необходимо, тк в библиотечных тайпингах указан неверный тип для children,
                который подразумевает один уровень вложенности для дерева, хотя поддержана неограниченная вложенность.
                переопределить библиотечный тип нельзя
               */
                  VirtualTreeHelpers.filterNodes(node.children as Array<T>, filter)
                : [];

            return !filter(node)
                ? [...filtered, ...filteredChildren]
                : [
                      ...filtered,
                      {
                          ...node,
                          children: filteredChildren
                      }
                  ];
        }, resultPhrases);
    }

    static removeNodes<T extends Node>(nodes: Array<T>, predicate: (node: T) => boolean): Array<T> {
        const result: Array<T> = [];

        nodes.forEach(node => {
            const newNode = {
                ...node
            };
            if (predicate(newNode)) {
                return;
            }

            if (newNode.children) {
                newNode.children = VirtualTreeHelpers.removeNodes(newNode.children, predicate);
            }

            result.push(newNode);
        });

        return result;
    }

    static isSelected(node: Node): boolean {
        return node.state && (node.state.selected || node.state.partialSelected);
    }

    static isMatched(search: string): (node: Node) => boolean {
        return (node: Node): boolean => {
            const upperCaseId = String(node.id).toUpperCase();
            const upperCaseName = unescapeHtmlSymbolCodes(node.name).toUpperCase();
            const upperCaseSearchTerm = search.toUpperCase().trim();

            return upperCaseName.indexOf(upperCaseSearchTerm) > -1 || upperCaseId.indexOf(upperCaseSearchTerm) > -1;
        };
    }

    static selectNodes<T extends Node>(nodes: Array<T>, updatedNodes: Array<T>): Array<T> {
        return nodes.map(node => {
            const updatedNode = updatedNodes.find(({ id }) => id === node.id);
            if (updatedNode) {
                return {
                    ...updatedNode,
                    state: {
                        ...updatedNode.state,
                        partialSelected: false
                    },
                    children: node.children
                        ? VirtualTreeHelpers.selectChildren(
                              node.children,
                              updatedNode.state && updatedNode.state.selected
                          )
                        : []
                };
            }

            if (node.children) {
                return {
                    ...node,
                    children: VirtualTreeHelpers.selectNodes(node.children, updatedNodes)
                };
            }

            return node;
        });
    }

    static generateParentsMap(nodes: Array<Node>): ParentsMap {
        const map = new Map();

        const fillMap = (innerNodes: Array<Node>, parents: Array<string | number> = []): void => {
            innerNodes.forEach(node => {
                if (node.children) {
                    fillMap(node.children, [...parents, node.id]);
                }

                map.set(node.id, parents);
            });
        };

        fillMap(nodes);

        return map;
    }

    static flattenTree<T extends Node>(tree: Array<T>): Array<T> {
        const resultTree: Array<T> = [];

        return tree.reduce((flattenedTree: Array<T>, node) => {
            if (node.children && node.children.length !== 0) {
                /* приведение типа необходимо, тк в библиотечных тайпингах указан неверный тип для children,
                который подразумевает один уровень вложенности для дерева, хотя поддержана неограниченная вложенность.
                переопределить библиотечный тип нельзя
               */
                return [...flattenedTree, node, ...VirtualTreeHelpers.flattenTree(node.children as Array<T>)];
            }

            return [...flattenedTree, node];
        }, resultTree);
    }

    static replaceNode<T extends Node>(tree: Array<T>, replacedNode: T): Array<T> {
        return tree.map(node => {
            if (node.id === replacedNode.id) {
                return replacedNode;
            }

            if (node.children) {
                return {
                    ...node,
                    children: VirtualTreeHelpers.replaceNode(node.children, replacedNode)
                };
            }

            return node;
        });
    }

    static updateNodes<T extends Node>(tree: Array<T>, nodesNames: Array<string>, updateParams: Partial<T>): Array<T> {
        return tree.map(node => {
            if (nodesNames.includes(node.name)) {
                return {
                    ...node,
                    ...updateParams
                };
            }

            if (node.children) {
                return {
                    ...node,
                    children: VirtualTreeHelpers.updateNodes(node.children, nodesNames, updateParams)
                };
            }

            return node;
        });
    }

    static expandNodes<T extends Node>(nodes: Array<T>, expandedMap: Record<string, string>): Array<T> {
        return nodes.map(node => {
            if (!expandedMap[node.name]) {
                return node;
            }

            return {
                ...node,
                state: {
                    ...node.state,
                    expanded: true
                }
            };
        });
    }

    static getNodesStateMap<T extends Node>(
        tree: Array<T>,
        defaultMap: Record<keyof T['state'], {}>
    ): Record<keyof T['state'], Record<string, boolean>> {
        const nodesStateMap = defaultMap;

        const fillChildren = (nodes: Array<T>): void => {
            nodes.forEach(node => {
                const nodeState = node.state || {};

                Object.keys(nodeState).forEach(stateKey => {
                    if (!nodeState[stateKey]) {
                        return;
                    }

                    if (!nodesStateMap[stateKey]) {
                        nodesStateMap[stateKey] = {};
                    }

                    nodesStateMap[stateKey][node.name] = true;
                });

                if (node.children) {
                    fillChildren(node.children as Array<T>);
                }
            });
        };

        fillChildren(tree);

        return nodesStateMap;
    }

    static getFlattenedTree(tree: Array<Node>): Array<Node> {
        return tree.reduce((flattenedTree, node) => {
            if (node.children && node.children.length !== 0) {
                return [...flattenedTree, node, ...VirtualTreeHelpers.getFlattenedTree(node.children)];
            }

            return [...flattenedTree, node];
        }, []);
    }
}
